/**
 * 編集用のクラス名を生成する関数
 * @param {string} name - フィールド名（スネークケース）
 * @returns {string} 編集用クラス名
 */
export const getTargetClassFromName = (name) => {
  return `edit_${name}`;
};

/**
メッセージタイプの定義
*/
export const MESSAGE_TYPES = {
  TEXT_UPDATE: "TEXT_UPDATE", // 文字列更新
  IMAGE_UPDATE: "IMAGE_UPDATE", // 画像更新
  VIDEO_UPDATE: "VIDEO_UPDATE", // 動画更新
  ARRAY_ITEM_UPDATE: "ARRAY_ITEM_UPDATE", // 配列要素の更新
  SCROLL_TO: "SCROLL_TO", // スクロール命令
  SELECT_INSTRUCTOR: "SELECT_INSTRUCTOR", // インストラクター選択
  DELETE_INSTRUCTOR: "DELETE_INSTRUCTOR", // インストラクター削除
  ADD_INSTRUCTOR: "ADD_INSTRUCTOR", // インストラクター追加
  SELECT_CONCEPT: "SELECT_CONCEPT", // コンセプト説明選択
  DELETE_CONCEPT: "DELETE_CONCEPT", // コンセプト説明削除
  ADD_CONCEPT: "ADD_CONCEPT", // コンセプト説明追加
  ADD_PRICE_SECTION: "ADD_PRICE_SECTION", // 料金セクションの追加
  DELETE_PRICE_SECTION: "DELETE_PRICE_SECTION", // 料金セクションの削除
  ADD_PRICE_ITEM: "ADD_PRICE_ITEM", // 料金項目の追加
  DELETE_PRICE_ITEM: "DELETE_PRICE_ITEM", // 料金項目の削除
  SELECT_PRICE_SECTION: "SELECT_PRICE_SECTION", // 料金セクションの選択
  ADD_QA_CATEGORY: "ADD_QA_CATEGORY", // Q&Aカテゴリーの追加
  DELETE_QA_CATEGORY: "DELETE_QA_CATEGORY", // Q&Aカテゴリーの削除
  ADD_QA_ITEM: "ADD_QA_ITEM", // Q&A項目の追加
  DELETE_QA_ITEM: "DELETE_QA_ITEM", // Q&A項目の削除
  MULTIPLE_IMAGES_UPDATE: "MULTIPLE_IMAGES_UPDATE", // 複数画像
  HASHTAG_UPDATE: "HASHTAG_UPDATE", // ハッシュタグ更新
};

/**
 * 入力変更時にiframeにメッセージを送信する関数
 * @param {Event} e - イベントオブジェクト
 * @param {Object} options - オプション
 * @param {string} options.contentKey - コンテンツのキー名
 * @param {Object} options.currentData - 現在のデータ
 * @param {Function} options.setData - データ更新関数
 * @returns {void}
 */
export const handleInputChangeWithScroll = (
  e,
  options,
  overrideFieldName = null
) => {
  const { contentKey = "pageContent", currentData, setData } = options;
  const { name, value } = e.target;
  const index = e.target.dataset.index;
  const eventType = e.type;

  // overrideFieldNameが指定されている場合はそれを使用、そうでなければnameを使用
  const fieldNameForMessage = overrideFieldName || name;

  // インストラクターコンテンツの場合の特別処理
  if (contentKey === "instructorContent" && index !== undefined) {
    // 配列要素の更新
    setData((prev) => {
      const newData = { ...prev };
      if (!newData.instructors) newData.instructors = [];

      const updatedInstructors = [...(newData.instructors || [])];
      if (!updatedInstructors[index]) updatedInstructors[index] = {};
      updatedInstructors[index] = {
        ...updatedInstructors[index],
        [name]: value,
      };

      newData.instructors = updatedInstructors;
      return newData;
    });

    const iframe = document.querySelector("iframe");
    if (iframe && iframe.contentWindow) {
      const targetSelector = `.${getTargetClassFromName(fieldNameForMessage)}`;

      if (eventType === "focus") {
        let tabValue = "";
        if (fieldNameForMessage === "profile") {
          tabValue = 1;
        }

        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.SELECT_INSTRUCTOR,
            contentKey,
            index: parseInt(index),
            instructor: currentData.instructors[index],
            target: targetSelector,
            tabValue,
            eventType: "input_focus", // 明確なeventTypeを設定
            preserveStudios: true,
          },
          process.env.REACT_APP_ORIGIN
        );
      } else {
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.ARRAY_ITEM_UPDATE,
            target: targetSelector,
            contentKey,
            fieldName: fieldNameForMessage,
            value,
            index: parseInt(index),
            instructor: currentData.instructors[index],
            eventType: "change",
            skipScroll: true,
            preserveTab: true,
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    }
  } else {
    // 通常のページ（topページなど）の処理
    console.log(`[3000] Processing normal page content for ${contentKey}`);

    // ステート更新
    setData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // iframeにメッセージを送信
    const iframe = document.querySelector("iframe");
    if (iframe && iframe.contentWindow) {
      const targetSelector = `.${getTargetClassFromName(name)}`;

      if (eventType === "focus") {
        // フォーカス時はスクロールメッセージを送信
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.SCROLL_TO,
            target: targetSelector,
            contentKey,
            fieldName: name,
            value,
            allData: { ...currentData, [name]: value },
          },
          process.env.REACT_APP_ORIGIN
        );
      } else {
        // 変更時はテキスト更新メッセージを送信
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            allData: { ...currentData, [name]: value },
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    }
  }
};

/**
 * 要素にスクロールするメッセージを送信する汎用関数
 * @param {string} fieldName - フィールド名
 * @param {*} value - 値（オプション）
 * @param {Object} options - オプション
 * @returns {void}
 */
export const sendScrollMessage = (fieldName, value = null, options = {}) => {
  const {
    contentKey = "pageContent",
    componentKey,
    messageType = MESSAGE_TYPES.SCROLL_TO,
    currentData = {},
  } = options;

  // iframe内の対応する要素にスクロールする
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    const targetClass = `.${getTargetClassFromName(fieldName)}`;

    // スクロールメッセージを送信
    iframe.contentWindow.postMessage(
      {
        type: messageType,
        target: targetClass,
        contentKey,
        componentKey,
        fieldName,
        value,
        allData: value ? { ...currentData, [fieldName]: value } : currentData,
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * 画像更新時にiframeにメッセージを送信し、対応する要素にスクロールする関数
 * @param {Object} imageData - 画像データ
 * @param {string} fieldName - フィールド名
 * @param {Object} options - オプション
 * @returns {void}
 */
export const handleImageUpdateWithScroll = (imageData, fieldName, options) => {
  const { setData, ...restOptions } = options;

  // ステート更新
  if (setData) {
    setData((prev) => ({ ...prev, [fieldName]: imageData }));
  }

  // スクロールメッセージを送信
  sendScrollMessage(fieldName, imageData, restOptions);
};

/**
 * 動画更新時にiframeにメッセージを送信し、対応する要素にスクロールする関数
 * @param {Object} videoData - 動画データ
 * @param {string} fieldName - フィールド名
 * @param {Object} options - オプション
 * @returns {void}
 */
export const handleVideoUpdateWithScroll = (videoData, fieldName, options) => {
  const { setData, ...restOptions } = options;

  // ステート更新
  if (setData) {
    setData((prev) => ({ ...prev, [fieldName]: videoData }));
  }

  // スクロールメッセージを送信
  sendScrollMessage(fieldName, videoData, {
    ...restOptions,
    messageType: MESSAGE_TYPES.VIDEO_UPDATE, // 既存のVIDEO_UPDATEを使用
  });
};

/**
 * 画像更新時にiframeにメッセージを送信する関数
 * @param {Object} imageData - 画像データ
 * @param {string} fieldName - フィールド名
 * @param {Object} options - オプション
 * @returns {void}
 */
export const handleImageUpdate = (imageData, fieldName, options) => {
  const {
    contentKey = "pageContent",
    componentKey,
    currentData,
    setData,
  } = options;

  // ステート更新
  setData((prev) => ({ ...prev, [fieldName]: imageData }));

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    const targetClass = `.${getTargetClassFromName(fieldName)}`;

    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.IMAGE_UPDATE,
        target: targetClass,
        contentKey,
        componentKey,
        fieldName,
        value: imageData,
        allData: { ...currentData, [fieldName]: imageData },
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * 動画更新時にiframeにメッセージを送信する関数
 * @param {Object} videoData - 動画データ
 * @param {string} fieldName - フィールド名
 * @param {Object} options - オプション
 * @returns {void}
 */
export const handleVideoUpdate = (videoData, fieldName, options) => {
  const { contentKey = "pageContent", currentData, setData } = options;

  // ステート更新
  setData((prev) => ({ ...prev, [fieldName]: videoData }));

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.VIDEO_UPDATE,
        contentKey,
        fieldName,
        value: videoData,
        allData: { ...currentData, [fieldName]: videoData },
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * iframeにデータを送信する関数
 * @param {Object} data - 送信するデータ
 * @param {string} contentKey - コンテンツのキー名
 * @param {string} origin - 送信先オリジン
 * @returns {void}
 */
export const sendDataToIframe = (data, contentKey = "pageContent", origin) => {
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    try {
      iframe.contentWindow.postMessage(
        {
          type: MESSAGE_TYPES.TEXT_UPDATE,
          contentKey,
          allData: data,
        },
        origin
      );
    } catch (error) {
      console.error("iframeへのメッセージ送信中にエラーが発生しました:", error);
    }
  }
};

/**
 * インストラクター削除時にiframeにメッセージを送信する関数
 * @param {number} instructorId - 削除するインストラクターのID
 * @param {Object} options - オプション
 * @returns {void}
 */
export const handleInstructorDelete = (instructorId, options) => {
  const { contentKey = "instructorContent" } = options;

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.DELETE_INSTRUCTOR,
        contentKey,
        instructorId,
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * インストラクター追加時にiframeにメッセージを送信する関数
 * @param {Object} instructor - 追加するインストラクターデータ
 * @param {number} index - 追加位置のインデックス
 * @param {Object} options - オプション
 * @returns {void}
 */
export const handleInstructorAdd = (instructor, index, options) => {
  const { contentKey = "instructorContent", currentData } = options;

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.ADD_INSTRUCTOR,
        contentKey,
        instructor,
        index,
        allInstructors: currentData.instructors, // 全インストラクターリスト
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * 料金セクションの追加処理を行う関数
 * @param {Object} section - 追加する料金セクション
 * @param {number} index - 追加位置
 * @param {Object} options - オプション
 */
export const handlePriceSectionAdd = (section, index, options) => {
  const { contentKey = "priceContent", currentData } = options;

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.ADD_PRICE_SECTION,
        contentKey,
        section,
        index,
        priceContent: currentData,
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * 料金セクションの削除処理を行う関数
 * @param {number} sectionId - 削除する料金セクションのID
 * @param {Object} options - オプション
 */
export const handlePriceSectionDelete = (sectionId, options) => {
  const { contentKey = "priceContent", currentData } = options;

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.DELETE_PRICE_SECTION,
        contentKey,
        sectionId,
        priceContent: currentData,
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * 料金項目の追加処理を行う関数
 * @param {Object} priceItem - 追加する料金項目
 * @param {number} itemIndex - 料金セクションのインデックス
 * @param {number} priceIndex - 追加位置
 * @param {Object} options - オプション
 */
export const handlePriceItemAdd = (
  priceItem,
  itemIndex,
  priceIndex,
  options
) => {
  const { contentKey = "priceContent", currentData } = options;

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.ADD_PRICE_ITEM,
        contentKey,
        priceItem,
        itemIndex,
        priceIndex,
        priceContent: currentData,
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * 料金項目の削除処理を行う関数
 * @param {number} itemIndex - 料金セクションのインデックス
 * @param {number} priceIndex - 削除する料金項目のインデックス
 * @param {Object} options - オプション
 */
export const handlePriceItemDelete = (itemIndex, priceIndex, options) => {
  const { contentKey = "priceContent", currentData } = options;

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.DELETE_PRICE_ITEM,
        contentKey,
        itemIndex,
        priceIndex,
        priceContent: currentData,
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * 料金項目の更新処理を行う関数
 * @param {number} itemIndex - 料金セクションのインデックス
 * @param {number} priceIndex - 料金項目のインデックス
 * @param {string} field - 更新するフィールド名
 * @param {*} value - 更新値
 * @param {Object} options - オプション
 */
export const handlePriceItemUpdate = (
  itemIndex,
  priceIndex,
  field,
  value,
  options
) => {
  const { contentKey = "priceContent", currentData } = options;

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    const targetSelector = `.edit_price_${itemIndex}_${priceIndex}_${field}`;

    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.ARRAY_ITEM_UPDATE,
        target: targetSelector,
        contentKey,
        fieldName: field,
        value,
        itemIndex,
        priceIndex,
        allData: { price_content: currentData },
      },
      process.env.REACT_APP_ORIGIN
    );

    // スクロール命令も送信
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.SCROLL_TO,
        target: targetSelector,
        contentKey,
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * Q&Aカテゴリーの追加処理を行う関数
 * @param {Object} category - 追加するQ&Aカテゴリー
 * @param {number} index - 追加位置
 * @param {Object} options - オプション
 */
export const handleQACategoryAdd = (category, index, options) => {
  const { contentKey = "qaContent", currentData } = options;

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.ADD_QA_CATEGORY,
        contentKey,
        category,
        index,
        qaCategories: currentData,
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * Q&Aカテゴリーの削除処理を行う関数
 * @param {number} categoryId - 削除するQ&AカテゴリーのID
 * @param {Object} options - オプション
 */
export const handleQACategoryDelete = (categoryId, options) => {
  const { contentKey = "qaContent", currentData } = options;

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.DELETE_QA_CATEGORY,
        contentKey,
        categoryId,
        qaCategories: currentData,
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * Q&A項目の追加処理を行う関数
 * @param {Object} qaItem - 追加するQ&A項目
 * @param {number} categoryIndex - Q&Aカテゴリーのインデックス
 * @param {number} qaIndex - 追加位置
 * @param {Object} options - オプション
 */
export const handleQAItemAdd = (qaItem, categoryIndex, qaIndex, options) => {
  const { contentKey = "qaContent", currentData } = options;

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.ADD_QA_ITEM,
        contentKey,
        qaItem,
        categoryIndex,
        qaIndex,
        qaCategories: currentData,
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};

/**
 * Q&A項目の削除処理を行う関数
 * @param {number} categoryIndex - Q&Aカテゴリーのインデックス
 * @param {number} qaIndex - 削除するQ&A項目のインデックス
 * @param {Object} options - オプション
 */
export const handleQAItemDelete = (categoryIndex, qaIndex, options) => {
  const { contentKey = "qaContent", currentData } = options;

  // iframeにメッセージを送信
  const iframe = document.querySelector("iframe");
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: MESSAGE_TYPES.DELETE_QA_ITEM,
        contentKey,
        categoryIndex,
        qaIndex,
        qaCategories: currentData,
      },
      process.env.REACT_APP_ORIGIN
    );
  }
};
